import {
  MenuProvider,
  MenuButton,
  MenuItem,
  Menu,
} from '@shared/components/Menu'
import {
  ChevronDown,
  FeatherIcon,
  LandmarkIcon,
  MegaphoneIcon,
  ScaleIcon,
  Scroll,
  ExternalLinkIcon,
  RadarIcon,
} from 'lucide-react'
import { Fragment } from 'react'
import { desktop } from '@/styles/theme.css'
import { BookMarked as LawIcon } from 'lucide-react'
import LogoBackdropBrand from '@/components/LogoBackdropBrand'
import { Box, Row, Stack } from '@christiankaindl/lyts'
import Link from 'next/link'
import { Menubar, PopoverArrow } from '@ariakit/react'
import { pathMappings } from '@shared/lib/paths'
import Text from '@shared/components/Text/Text'
import bg from '@/public/assets/amiga-bg.jpg'
import Image from 'next/image'
import codaraLogo from '@shared/assets/codara-logo.svg'
import CodaraStripes from './CodaraStripes'
import Button from '@shared/components/Button/Button'

export default function NavBarMenus() {
  return (
    <Row gap='1px' className={desktop} asChild>
      <Menubar>
        <ProductsMenu />
        <LegalMenu />
        <ResourcesMenu />
        <Button variant='flat' href='/ueber-uns' style={{ fontWeight: 400 }}>
          Über uns
        </Button>
      </Menubar>
    </Row>
  )
}

function ProductsMenu() {
  return (
    <MenuProvider>
      <MenuButton showOnHover style={{ fontWeight: 400 }}>
        Codara{' '}
        <ChevronDown
          size={16}
          style={{ marginRight: -4, marginLeft: -4, marginBottom: -2 }}
        />
      </MenuButton>

      <Menu
        shift={-128}
        style={{ maxWidth: 'unset', padding: 12, borderRadius: 25 }}
      >
        {/* <PopoverArrow /> */}
        <Row style={{ minWidth: 525 }} gap={0}>
          <Row
            xAlign='space-between'
            style={{
              position: 'relative',
              zIndex: 1,
              padding: '18px 24px',
              borderRadius: 15,
              boxShadow: 'inset 0px 0px 1px rgb(0 0 0 / 0.3)',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <CodaraStripes />
            <Stack gap={0.5} xAlign='center'>
              <Image
                src={codaraLogo}
                alt='Codara Logo'
                width={100}
                height={30}
                style={{ objectFit: 'contain', marginBottom: 8 }}
                priority
              />
              <Text color='secondary' size='small' align='center'>
                KI-gestützte Lösungen für
                <br />
                effizientere Legal Compliance
              </Text>
              <Text size='small'>
                <Row gap={0.5} asChild>
                  <Link href='https://codara.com/' target='_blank'>
                    Mehr erfahren <ExternalLinkIcon size={16} />
                  </Link>
                </Row>
              </Text>
            </Stack>

            <Stack gap={0.25}>
              <RichMenuItem
                title='Regulatory Monitoring'
                description='All-in-one Regulatory Compliance'
                icon={LawIcon}
                href='https://codara.com/produkte/regulatory-monitoring'
              />
              <RichMenuItem
                title='Judikatur Monitoring'
                description='AI-powered Entscheidungs-Monitoring'
                icon={ScaleIcon}
                href='https://codara.com/produkte/judikatur-monitoring'
              />
              <RichMenuItem
                title='Legislative Radar'
                description='Gesetzgebung KI-gestützt managen'
                icon={RadarIcon}
                href='https://codara.com/produkte/regulatory-radar'
              />
            </Stack>
            <Image
              priority
              src={bg}
              fill
              alt=''
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
                zIndex: -1,
                opacity: 0.4,
                maskImage: 'linear-gradient(to top, transparent, white 30%)',
              }}
            />
          </Row>

          <div />
        </Row>
      </Menu>
    </MenuProvider>
  )
}

const menuIconMappings: Record<string, typeof LawIcon> = {
  verordnungen: Scroll,
  vertraege: FeatherIcon,
  vereinbarungen: FeatherIcon,
  kundmachungen: MegaphoneIcon,
}

function LegalMenu() {
  const categories = Object.entries(pathMappings)
    .map(([key, category]) => {
      const allPaths = Object.entries(category.paths)
        .filter(([, { hidden }]) => !hidden)
        .map(([pathKey, { name }]) => ({ key: pathKey, name }))
      return {
        key,
        category,
        paths: allPaths.slice(0, 5),
        totalCount: allPaths.length,
      }
    })
    .filter(({ paths }) => paths.length > 0)

  return (
    <MenuProvider>
      <MenuButton showOnHover style={{ fontWeight: 400 }}>
        Recht{' '}
        <ChevronDown
          size={16}
          style={{ marginRight: -4, marginLeft: -4, marginBottom: -2 }}
        />
      </MenuButton>
      <Menu
        shift={-64}
        unmountOnHide={false}
        style={{ maxWidth: 'unset', padding: 0, borderRadius: 21 }}
      >
        <PopoverArrow />
        <Row
          yAlign='start'
          style={{ padding: 15, maxWidth: 825, overflowX: 'auto' }}
        >
          {categories.map(({ key, category, paths, totalCount }, index) => {
            const isLawCategory = category.documentType.includes('law')
            const remaining = totalCount - paths.length
            return (
              <Fragment key={key}>
                {index > 0 && <div />}
                <Stack
                  gap='1px'
                  style={isLawCategory ? undefined : { minWidth: 180 }}
                >
                  <Link
                    href={`/${key}`}
                    style={{
                      fontSize: 'large',
                      padding: '6px 12px',
                      display: 'inline-block',
                    }}
                  >
                    <b>{category.name}</b>
                  </Link>
                  {paths.map(({ key: pathKey, name }) => {
                    const Icon = isLawCategory
                      ? (menuIconMappings[pathKey] ?? LawIcon)
                      : LandmarkIcon
                    return (
                      <MenuItem key={pathKey} href={`/${key}/${pathKey}`}>
                        <Icon color='#987C4D' size={16} width={16} />
                        <Row gap={0.5}>{name}</Row>
                      </MenuItem>
                    )
                  })}
                  {remaining > 0 && (
                    <MenuItem href={`/${key}`}>
                      <Text size='small' color='tertiary'>
                        {remaining} mehr
                      </Text>
                    </MenuItem>
                  )}
                </Stack>
              </Fragment>
            )
          })}
        </Row>
      </Menu>
    </MenuProvider>
  )
}

function ResourcesMenu() {
  return (
    <MenuProvider>
      <MenuButton showOnHover style={{ fontWeight: 400 }}>
        Themen{' '}
        <ChevronDown
          size={16}
          style={{ marginRight: -4, marginLeft: -4, marginBottom: -2 }}
        />
      </MenuButton>
      <Menu unmountOnHide={false} style={{ padding: 15, borderRadius: 21 }}>
        <PopoverArrow />
        <div
          style={{
            fontSize: 'large',
            padding: '6px 12px',
            display: 'inline-block',
          }}
        >
          <b>Themen</b>
        </div>
        <MenuItem href='/blog'>
          <Row gap={0.5}>Blog</Row>
        </MenuItem>
        <MenuItem href='/themen/grundlagen-des-rechts'>
          <Row gap={0.5}>Grundlagen des Rechts</Row>
        </MenuItem>
        <MenuItem iconColor='#987C4D' href='/themen/schule'>
          <Row gap={0.5}>Schule</Row>
        </MenuItem>
        <MenuItem iconColor='#987C4D' href='/themen/studium'>
          <Row gap={0.5}>Studium</Row>
        </MenuItem>
        <MenuItem iconColor='#987C4D' href='/themen/strafrecht'>
          <Row gap={0.5}>Strafrecht</Row>
        </MenuItem>
      </Menu>
    </MenuProvider>
  )
}

function RichMenuItem({
  title,
  description,
  icon: Icon,
  iconBackdrop: IconBackdrop = LogoBackdropBrand,
  iconColor = '#987C4D',
  href,
}) {
  return (
    <Row gap={0.5} asChild>
      <MenuItem
        href={href}
        {...(href?.startsWith('https://')
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
        style={{
          paddingTop: 6,
          paddingBottom: 6,
          maxWidth: '18em',
          borderRadius: 9,
        }}
      >
        <Box
          style={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}
          xAlign='center'
          yAlign='center'
        >
          <IconBackdrop
            height={36}
            width={36}
            style={{ position: 'absolute', top: 0, left: 0 }}
          />
          <Icon height={16} width={16} color={iconColor} />
        </Box>
        <Stack gap={0}>
          {title}
          <Text
            size='small'
            color='tertiary'
            style={{ textWrap: 'wrap', lineHeight: 1.25 }}
          >
            {description}
          </Text>
        </Stack>
      </MenuItem>
    </Row>
  )
}
